import {
  QueryKey,
  QueryFunction,
  useSuspenseInfiniteQuery,
  UseInfiniteQueryResult,
  useMutation,
  useSuspenseQuery,
  UseSuspenseQueryOptions,
  UseSuspenseInfiniteQueryOptions,
  UseQueryResult,
  useQueryClient,
  InfiniteData,
} from '@tanstack/react-query'
import { format } from 'date-fns'
import { TablePagination, TableSort, Weekday } from 'zens-storybook'
import axios from 'axios'
import { createQueryKeyStore } from '@lukemorales/query-key-factory'

import {
  Company,
  CompanyData,
  Location,
  LocationData,
  Building,
  BuildingData,
  Floor,
  FloorData,
  User,
  UserData,
  Workspace,
  WorkspaceData,
  WorkspaceTypeData,
  WorkspaceType,
  UserInviteData,
  ZoneData,
  Zone,
  Product,
  ProductData,
  ProductType,
  ProductTypeData,
  Schedule,
  ScheduleData,
  WorkspaceSchedule,
  InsightData,
  InsightTime,
  InsightWorkspaceData,
  InsightWorkspace,
  InsightTrend,
  InsightTrendData,
  LoggingMessageData,
  LoggingMessage,
  CommentData,
  Comment,
  PhotoData,
  Photo,
  WorkspaceTypeElement,
  WorkspaceTypeElementData,
  FloorplanData,
  Floorplan,
  InsightOverview,
  InsightOverviewData,
  Trigger,
  TriggerData,
  TimeZone,
  Country,
} from '../data'
import { Profile, ProfileData, ProfileDataEmail, ProfileDataPassword } from '../auth'
import { Draft, Model, ModelData } from '../general'
import { useStore } from '../root'

interface SearchModel {
  searchText: string
}

interface MetaResponse {
  current_page: number
  per_page: number
  total: number
}

interface Meta {
  page: number
  size: number
  total: number
}

export interface WorkspacesFilter {
  search?: string
  locationId?: number[]
  buildingId?: number[]
  floorId?: number[]
  zoneId?: number[]
  workspaceTypeId?: number[]
  isInstalled?: boolean
  connectivityState?: number[]
  peripheralsState?: number[]
  occupancyState?: number[]
}

export type ProductsFilter = {
  search?: string
  floorId?: number[]
  isInstalled?: boolean
  connectivityState?: number[]
  peripheralsState?: number[]
}

interface Mutation<T extends ModelData, A> {
  action: A
  draft: Draft<T>
  other?: any
}

export interface ProductTransferData extends ProductData {
  transfer_company_id: number
}

export type InsightTrendId = 'floor' | 'zone' | 'workspacetype' | 'weekday'
export type InsightTimeType = 'day' | 'hour'

export type DataWithUpload<T> = T & {
  upload?: File | { uri?: string; name?: string }
  progress?: number
  error?: string
}

interface UseQueryOptions extends Omit<UseSuspenseQueryOptions, 'queryKey' | 'queryFn'> {
  enabled?: boolean
}

interface UseInfiniteQueryOptions
  extends Omit<UseSuspenseInfiniteQueryOptions, 'queryKey' | 'queryFn' | 'initialPageParam' | 'getNextPageParam'> {
  enabled?: boolean
}

const defaultOptions: Omit<UseSuspenseQueryOptions, 'queryKey' | 'queryFn'> = {
  staleTime: 10000,
  retry: false,
}

export async function fetchAllPages<T = any>(request: (page: number) => Promise<{ data: T[]; meta: Meta }>) {
  let page = 0
  let data: T[] = []
  let result
  do {
    result = await request(page)
    data = [...data, ...result.data]
    page++
  } while (data.length < result.meta.total && result.data.length > 0)
  return data
}

function searchResult<T extends SearchModel>(result: UseQueryResult<T[]>, search?: string) {
  if (search) {
    return {
      ...result,
      data: result.data?.filter((item) => item.searchText?.toLowerCase().indexOf(search.toLowerCase()) >= 0),
    }
  }
  return result
}

function useModelMutation<T extends ModelData, R extends Model<T>, A extends string = 'create' | 'update' | 'delete'>(
  config: Record<A, (draft: Draft<T>, other?: any) => Promise<R | void>>,
  invalidateQueries?: (mutation: Mutation<T, A>) => QueryKey[],
) {
  const queryClient = useQueryClient()

  const { mutateAsync, ...otherMutation } = useMutation({
    mutationFn: async (mutation: Mutation<T, A>) => {
      const { action, draft, other } = mutation
      return config[action]?.(draft, other)
    },
    onSuccess: (_, mutation) => {
      invalidateQueries?.(mutation).forEach((queryKey) => {
        console.info('[QUERY] Invalidate query: ', queryKey.join(', '))
        queryClient.invalidateQueries({ queryKey })
      })
      mutation.draft.settle()
    },
  })

  const mutation = Object.keys(config).reduce(
    (acc, action) => ({
      ...acc,
      [action]: async (draft: Draft<T>, other?: any) => {
        const response = await mutateAsync({
          action,
          draft,
          other,
        } as Mutation<T, A>)
        return response
      },
    }),
    {} as Record<A, (draft: Draft<T>, other?: any) => R>,
  )
  return {
    ...otherMutation,
    ...mutation,
  }
}

function useModelQuery<T>(query: { queryKey: QueryKey; queryFn: QueryFunction<T, any> }, options: UseQueryOptions) {
  const { queryFn, queryKey, ...otherQuery } = query
  const { enabled, ...otherOptions } = options

  return useSuspenseQuery({
    ...otherQuery,
    ...otherOptions,
    ...defaultOptions,
    queryFn: enabled === false ? () => null : queryFn,
    queryKey: enabled === false ? [...queryKey, 'disabled'] : queryKey,
  }) as UseQueryResult<T>
}

function useModelInfiniteQuery<T>(
  query: { queryKey: QueryKey; queryFn: QueryFunction<T, any> },
  options: UseInfiniteQueryOptions,
) {
  const { queryFn, ...otherQuery } = query
  const { enabled, ...otherOptions } = options

  return useSuspenseInfiniteQuery({
    ...otherQuery,
    ...otherOptions,
    // @ts-expect-error Something is wrong with the type definitions
    queryFn: enabled === false ? () => undefined : queryFn,
    initialPageParam: 0,
    getNextPageParam: (page) => {
      const lastPage = page as { meta: Meta }
      return lastPage?.meta && lastPage?.meta.page * lastPage?.meta.size < lastPage?.meta.total
        ? lastPage?.meta.page
        : undefined
    },
  }) as UseInfiniteQueryResult<
    InfiniteData<{
      data: T[]
      meta: Meta
    }>
  >
}

export function useFetchWorkspaces() {
  const store = useStore()

  return async (request: {
    companyId: number
    pagination?: TablePagination
    sort?: TableSort
    filter?: WorkspacesFilter
    notInZone?: boolean
  }) => {
    const { companyId, pagination, sort, filter = {} } = request

    function filterParamForKey(key: keyof WorkspacesFilter) {
      switch (key) {
        case 'search':
          return 'search_query'
        case 'locationId':
          return 'location_id'
        case 'buildingId':
          return 'building_id'
        case 'floorId':
          return 'floor_id'
        case 'zoneId':
          return 'zone_id'
        case 'workspaceTypeId':
          return 'workspace_type_id'
        case 'isInstalled':
          return 'is_installed'
        case 'connectivityState':
          return 'connectivity_state'
        case 'peripheralsState':
          return 'peripherals_state'
        case 'occupancyState':
          return 'occupancy_state'
      }
    }

    const params = (Object.keys(filter) as (keyof WorkspacesFilter)[]).map(
      (key) =>
        `filter[${filterParamForKey(key)}]=${Array.isArray(filter[key]) ? (filter[key] as number[]).join(',') : filter[key]}`,
    )
    if (pagination?.page !== undefined) params.push(`page=${pagination?.page + 1}`)
    if (pagination?.size) params.push(`pagesize=${pagination?.size}`)
    if (sort?.field) params.push(`sort=${sort?.field}`)
    if (sort?.direction) params.push(`direction=${sort?.direction}`)
    if (request.notInZone !== undefined) params.push(`where_not_zone=${request.notInZone}`)

    const response = await store.api.get<
      | {
          data: WorkspaceData[]
          meta: MetaResponse
        }
      | WorkspaceData[]
    >(`company/${companyId}/workspaces${params.length ? `?${params.join('&')}` : ''}`)

    if (Array.isArray(response)) {
      return {
        data: response?.map((item) => new Workspace(store, item)),
      }
    }
    const { data, meta } = response
    return {
      data: data?.map((item) => new Workspace(store, item)),
      meta: {
        page: meta.current_page,
        size: meta.per_page,
        total: meta.total,
      } as Meta,
    }
  }
}

export function useFetchProducts() {
  const store = useStore()

  return async (request: {
    companyId: number
    pagination?: TablePagination
    sort?: TableSort
    filter?: ProductsFilter
  }) => {
    const { companyId, pagination, sort, filter = {} } = request

    function filterParamForKey(key: keyof ProductsFilter) {
      switch (key) {
        case 'search':
          return 'search_query'
        case 'floorId':
          return 'floor_id'
        case 'isInstalled':
          return 'is_installed'
        case 'connectivityState':
          return 'connectivity_state'
        case 'peripheralsState':
          return 'peripherals_state'
      }
    }

    const params = (Object.keys(filter) as (keyof ProductsFilter)[]).map(
      (key) =>
        `filter[${filterParamForKey(key)}]=${Array.isArray(filter[key]) ? (filter[key] as number[]).join(',') : filter[key]}`,
    )
    if (pagination?.page !== undefined) params.push(`page=${pagination?.page + 1}`)
    if (pagination?.size) params.push(`pagesize=${pagination?.size}`)
    if (sort?.field) params.push(`sort=${sort?.field}`)
    if (sort?.direction) params.push(`direction=${sort?.direction}`)

    const response = await store.api.get<
      | {
          data: ProductData[]
          meta: MetaResponse
        }
      | ProductData[]
    >(`company/${companyId}/products${params.length ? `?${params.join('&')}` : ''}`)
    if (Array.isArray(response)) {
      return {
        data: response?.map((item) => new Product(store, item)),
      }
    }
    const { data, meta } = response
    return {
      data: data?.map((item) => new Product(store, item)),
      meta: {
        page: meta.current_page,
        size: meta.per_page,
        total: meta.total,
      } as Meta,
    }
  }
}

export function useFetchProductLogging() {
  const store = useStore()

  return async (request: { companyId: number; productId: number; pagination?: TablePagination; sort?: TableSort }) => {
    const { companyId, productId, pagination, sort } = request
    const { page = 0, size = 10 } = pagination || {}
    const { data, meta } = await store.api.get<{
      data: LoggingMessageData[]
      meta: MetaResponse
    }>(
      `company/${companyId}/products/${productId}/messages?pagesize=${size}&page=${(page || 0) + 1}&sort=${
        sort?.field
      }&direction=${sort?.direction}`,
    )
    return {
      data: data.map((item) => new LoggingMessage(store, item)),
      meta: {
        page: meta.current_page,
        size: meta.per_page,
        total: meta.total,
      } as Meta,
    }
  }
}

export function useQueries() {
  const store = useStore()
  const fetchWorkspaces = useFetchWorkspaces()
  const fetchProducts = useFetchProducts()
  const fetchProductLogging = useFetchProductLogging()

  return createQueryKeyStore({
    profile: {
      detail: (token?: string) => ({
        queryKey: [undefined],
        queryFn: async () =>
          new Profile(store, await store.api.get<ProfileData>('profile', token ? { token } : undefined)),
      }),
    },
    companies: {
      detail: (companyId: number) => ({
        queryKey: [companyId],
        queryFn: async () => new Company(store, await store.api.get<CompanyData>(`companies/${companyId}`)),
        contextQueries: {
          insightsOverview: (
            buildingId: number,
            from: string,
            to: string,
            workingHours: boolean,
            weekdays?: Weekday[],
          ) => ({
            queryKey: [buildingId, from, to, workingHours, weekdays],
            queryFn: async () =>
              new InsightOverview(
                store,
                await store.api.get<InsightOverviewData>(
                  `company/${companyId}/insights/buildings/${buildingId}/overview?from=${from}&to=${to}&workhours=${workingHours}&weekdays=${weekdays?.join(',')}`,
                ),
              ),
          }),
          insightsTime: (
            from: string,
            to: string,
            buildingId?: number,
            workingHours?: boolean,
            weekdays?: Weekday[],
            floorId?: number,
            timeType?: InsightTimeType,
            workspaceId?: number,
          ) => ({
            queryKey: [buildingId, floorId, timeType, workspaceId, from, to, workingHours, weekdays],
            queryFn: async () =>
              (
                await store.api.get<InsightData[]>(
                  `${
                    floorId
                      ? `company/${companyId}/insights/floors/${floorId}`
                      : `company/${companyId}/insights/buildings/${buildingId}`
                  }?from=${from}&to=${to}&x=${timeType}&workhours=${workingHours}&weekdays=${weekdays}${
                    workspaceId ? `&workspace=${workspaceId}` : ''
                  }`,
                )
              ).map((item) => new InsightTime(store, item)),
          }),
          insightsFloor: (floorId: number, from: string, to: string, workingHours: boolean, weekdays?: Weekday[]) => ({
            queryKey: [floorId, from, to, workingHours, weekdays],
            queryFn: async () =>
              (
                await store.api.get<InsightWorkspaceData[]>(
                  `company/${companyId}/insights/floors/${floorId}/workspaces?from=${from}&to=${to}&workhours=${workingHours}&weekdays=${weekdays}`,
                )
              ).map((item) => new InsightWorkspace(store, item)),
          }),
          insightsTrend: (
            buildingId: number,
            trendId: InsightTrendId,
            from: string,
            to: string,
            workingHours: boolean,
            weekdays?: Weekday[],
          ) => ({
            queryKey: [buildingId, trendId, from, to, workingHours, weekdays],
            queryFn: async () =>
              (
                await store.api.get<InsightTrendData[]>(
                  `company/${companyId}/insights/buildings/${buildingId}/${
                    trendId === 'weekday'
                      ? 'weekdays'
                      : trendId === 'zone'
                        ? 'zones'
                        : trendId === 'workspacetype'
                          ? 'workspacetypes'
                          : 'floors'
                  }?from=${from}&to=${to}&workhours=${workingHours}&weekdays=${weekdays}`,
                )
              ).map((item) => new InsightTrend(store, item)),
          }),
          location: (locationId: number) => ({
            queryKey: [locationId],
            queryFn: async () =>
              new Location(store, {
                ...(await store.api.get<LocationData>(`company/${companyId}/locations/${locationId}`)),
                company_id: companyId,
              }),
            contextQueries: {
              buildings: {
                queryKey: [undefined],
                queryFn: async () =>
                  (await store.api.get<BuildingData[]>(`company/${companyId}/locations/${locationId}/buildings`)).map(
                    (data) => new Building(store, { ...data, company_id: companyId }),
                  ),
              },
            },
          }),
          locations: {
            queryKey: [undefined],
            queryFn: async () =>
              (await store.api.get<LocationData[]>(`company/${companyId}/locations`)).map(
                (data) => new Location(store, { ...data, company_id: companyId }),
              ),
          },
          building: (buildingId: number) => ({
            queryKey: [buildingId],
            queryFn: async () =>
              new Building(store, {
                ...(await store.api.get<BuildingData>(`company/${companyId}/buildings/${buildingId}`)),
                company_id: companyId,
              }),
            contextQueries: {
              floors: {
                queryKey: [undefined],
                queryFn: async () =>
                  (await store.api.get<FloorData[]>(`company/${companyId}/buildings/${buildingId}/floors`))
                    .sort((a, b) => (a.number || 0) - (b.number || 0))
                    .map((data) => new Floor(store, { ...data, company_id: companyId })),
              },
              floorplans: {
                queryKey: [undefined],
                queryFn: async () =>
                  (await store.api.get<FloorplanData[]>(`company/${companyId}/buildings/${buildingId}/floorplans`)).map(
                    (item) =>
                      new Floorplan(store, {
                        ...item,
                        company_id: companyId,
                      }),
                  ),
              },
            },
          }),
          buildings: {
            queryKey: [undefined],
            queryFn: async () =>
              (await store.api.get<BuildingData[]>(`company/${companyId}/buildings`)).map(
                (data) => new Building(store, { ...data, company_id: companyId }),
              ),
          },
          floor: (floorId: number) => ({
            queryKey: [floorId],
            queryFn: async () =>
              new Floor(store, {
                ...(await store.api.get<FloorData>(`company/${companyId}/floors/${floorId}`)),
                company_id: companyId,
              }),
          }),
          floors: {
            queryKey: [undefined],
            queryFn: async () =>
              (await store.api.get<FloorData[]>(`company/${companyId}/floors`))
                .sort((a, b) => (a.number || 0) - (b.number || 0))
                .map((data) => new Floor(store, { ...data, company_id: companyId })),
          },
          workspace: (workspaceId: number) => ({
            queryKey: [workspaceId],
            queryFn: async () =>
              new Workspace(
                store,
                await store.api.get<WorkspaceData>(`company/${companyId}/workspaces/${workspaceId}`),
              ),
            contextQueries: {
              zones: {
                queryKey: [undefined],
                queryFn: async () =>
                  (await store.api.get<ZoneData[]>(`company/${companyId}/workspaces/${workspaceId}/zones`)).map(
                    (item) => new Zone(store, { ...item, company_id: companyId }),
                  ),
              },
              schedule: {
                queryKey: [undefined],
                queryFn: async () =>
                  await store.api.get<WorkspaceSchedule>(
                    `company/${companyId}/workspaces/${workspaceId}/visual_program`,
                  ),
              },
              comments: (pagination: TablePagination) => ({
                queryKey: [pagination],
                queryFn: async () =>
                  (
                    await store.api.get<CommentData[]>(
                      `company/${companyId}/workspaces/${workspaceId}/comments?size=${pagination.size}&page=${pagination.page + 1}`,
                    )
                  ).map((item) => new Comment(store, { ...item, company_id: companyId })),
              }),
              photos: (pagination: TablePagination) => ({
                queryKey: [pagination],
                queryFn: async () =>
                  (
                    await store.api.get<PhotoData[]>(
                      `company/${companyId}/workspaces/${workspaceId}/photos?size=${pagination.size}&page=${pagination.page + 1}`,
                    )
                  ).map((item) => new Photo(store, { ...item, company_id: companyId })),
              }),
              triggers: {
                queryKey: [undefined],
                queryFn: async () =>
                  (
                    await store.api.get<TriggerData[]>(
                      `company/${companyId}/workspaces/${workspaceId}/settings/triggers`,
                    )
                  ).map((data) => new Trigger(store, data)),
              },
            },
          }),
          workspaces: (
            pagination?: TablePagination,
            sort?: TableSort,
            filter?: WorkspacesFilter,
            notInZone?: boolean,
          ) => ({
            queryKey: [pagination, sort, filter, notInZone],
            queryFn: () => fetchWorkspaces({ companyId, pagination, sort, filter, notInZone }),
            contextQueries: {
              infinite: {
                queryKey: [undefined],
                queryFn: ({ pageParam }) =>
                  fetchWorkspaces({
                    companyId,
                    pagination: { page: pageParam as number, size: pagination!.size },
                    sort,
                    filter,
                    notInZone,
                  }),
              },
            },
          }),
          zone: (zoneId: number) => ({
            queryKey: [zoneId],
            queryFn: async () =>
              new Zone(store, {
                ...(await store.api.get<ZoneData>(`company/${companyId}/zones/${zoneId}`)),
                company_id: companyId,
              }),
          }),
          zones: {
            queryKey: [undefined],
            queryFn: async () =>
              (await store.api.get<ZoneData[]>(`company/${companyId}/zones`)).map(
                (item) => new Zone(store, { ...item, company_id: companyId }),
              ),
            contextQueries: {
              floor: (floorId: number) => ({
                queryKey: [floorId],
                queryFn: async () =>
                  (await store.api.get<ZoneData[]>(`company/${companyId}/zones?filter[floor_id]=${floorId}`)).map(
                    (item) => new Zone(store, { ...item, company_id: companyId }),
                  ),
              }),
            },
          },
          floorplan: (floorId: number) => ({
            queryKey: [floorId],
            queryFn: async () =>
              new Floorplan(store, {
                ...(await store.api.get<FloorplanData>(`company/${companyId}/floorplans/${floorId}`)),
                company_id: companyId,
              }),
          }),
          product: (productId: number) => ({
            queryKey: [productId],
            queryFn: async () =>
              new Product(store, await store.api.get<ProductData>(`company/${companyId}/products/${productId}`)),
            contextQueries: {
              comments: (pagination: TablePagination) => ({
                queryKey: [pagination],
                queryFn: async () =>
                  (
                    await store.api.get<CommentData[]>(
                      `company/${companyId}/products/${productId}/comments?size=${pagination.size}&page=${pagination.page + 1}`,
                    )
                  ).map((item) => new Comment(store, { ...item, company_id: companyId })),
              }),
              photos: (pagination: TablePagination) => ({
                queryKey: [pagination],
                queryFn: async () =>
                  (
                    await store.api.get<PhotoData[]>(
                      `company/${companyId}/products/${productId}/photos?size=${pagination.size}&page=${pagination.page + 1}`,
                    )
                  ).map((item) => new Photo(store, { ...item, company_id: companyId })),
              }),
              logging: (pagination?: TablePagination, sort?: TableSort) => ({
                queryKey: [pagination, sort],
                queryFn: async () => fetchProductLogging({ companyId, productId, pagination, sort }),
                contextQueries: {
                  infinite: {
                    queryKey: [undefined],
                    queryFn: ({ pageParam }) =>
                      fetchProductLogging({
                        companyId,
                        productId,
                        pagination: { page: pageParam as number, size: pagination!.size },
                        sort,
                      }),
                  },
                },
              }),
            },
          }),
          products: (pagination?: TablePagination, sort?: TableSort, filter?: ProductsFilter) => ({
            queryKey: [pagination, sort, filter],
            queryFn: () => fetchProducts({ companyId, pagination, sort, filter }),
            contextQueries: {
              infinite: {
                queryKey: [undefined],
                queryFn: ({ pageParam }) =>
                  fetchProducts({
                    companyId,
                    pagination: { page: pageParam as number, size: pagination!.size },
                    sort,
                    filter,
                  }),
              },
            },
          }),
          schedule: (scheduleId: number) => ({
            queryKey: [scheduleId],
            queryFn: async () =>
              new Schedule(store, {
                ...(await store.api.get<ScheduleData>(`company/${companyId}/settings/schedule/${scheduleId}`)),
                company_id: companyId,
              }),
          }),
          schedules: {
            queryKey: [undefined],
            queryFn: async () =>
              (await store.api.get<ScheduleData[]>(`company/${companyId}/settings/schedule`)).map(
                (item) => new Schedule(store, { ...item, company_id: companyId }),
              ),
          },
          user: (userId: number) => ({
            queryKey: [userId],
            queryFn: async () => new User(store, await store.api.get<UserData>(`company/${companyId}/users/${userId}`)),
          }),
          users: {
            queryKey: [undefined],
            queryFn: async () =>
              (await store.api.get<UserData[]>(`company/${companyId}/users`)).map((data) => new User(store, data)),
          },
        },
      }),
      list: {
        queryKey: [undefined],
        queryFn: async () => (await store.api.get<CompanyData[]>('companies')).map((data) => new Company(store, data)),
      },
    },
    workspaceTypes: {
      detail: (workspaceTypeId: number) => ({
        queryKey: [workspaceTypeId],
        queryFn: async () =>
          new WorkspaceType(store, await store.api.get<WorkspaceTypeData>(`workspacetypes/${workspaceTypeId}`)),
      }),
      list: {
        queryKey: [undefined],
        queryFn: async () =>
          (await store.api.get<WorkspaceTypeData[]>(`workspacetypes`)).map((data) => new WorkspaceType(store, data)),
      },
    },
    productTypes: {
      list: {
        queryKey: [undefined],
        queryFn: async () =>
          (await store.api.get<ProductTypeData[]>(`catalog/productTypes`)).map((item) => new ProductType(store, item)),
      },
    },
    floorplans: {
      assets: (uri: string) => ({
        queryKey: [uri],
        queryFn: async () => {
          const { data } = await axios.get(uri)
          const [_, svg] = data.match(/<svg.*?>(.*)<\/svg>/is) || []
          const [__, width] = data.match(/<svg[^>]*?width="(.*?)"/is) || []
          const [___, height] = data.match(/<svg[^>]*?height="(.*?)"/is) || []
          const [____, viewboxWidth, viewboxHeight] = data.match(/<svg.*?viewbox=".+? .+? (.+?) (.+?)"/is) || []
          const asset = {
            width: parseInt(width, 10),
            height: parseInt(height, 10),
            svg,
          }
          if (isNaN(asset.width)) asset.width = parseInt(viewboxWidth, 10)
          if (isNaN(asset.height)) asset.height = parseInt(viewboxHeight, 10)
          return asset
        },
      }),
    },
    elements: {
      detail: (elementId: number) => ({
        queryKey: [elementId],
        queryFn: async () =>
          new WorkspaceTypeElement(
            store,
            await store.api.get<WorkspaceTypeElementData>(`workspacetypes/elements/${elementId}`),
          ),
      }),
      list: {
        queryKey: [undefined],
        queryFn: async () =>
          (await store.api.get<WorkspaceTypeElementData[]>(`workspacetypes/elements`)).map(
            (data) => new WorkspaceTypeElement(store, data),
          ),
      },
    },
    timezone: {
      detail: (lat: number, lng: number) => ({
        queryKey: [lat, lng, store.i18n.language],
        queryFn: async () => store.geo.getTimeZone({ lat, lng }),
      }),
    },
    country: {
      detail: (lat: number, lng: number) => ({
        queryKey: [lat, lng, store.i18n.language],
        queryFn: async () => store.geo.getCountry({ lat, lng }),
      }),
    },
  })
}

export function useProfile(request: { token?: string } = {}, options?: UseQueryOptions) {
  const { token } = request
  const { auth } = useStore()
  const queries = useQueries()
  return useModelQuery<Profile>(queries.profile.detail(token), {
    ...options,
    enabled: options?.enabled !== false && (auth.isSignedIn || !!token),
  })
}

export function useProfileMutation() {
  const store = useStore()
  return useModelMutation<ProfileData, Profile, 'update' | 'updatePassword' | 'updateEmail' | 'signOut'>({
    update: async (draft: Draft<ProfileData>) =>
      new Profile(store, await store.api.put<ProfileData>(`profile`, draft.data)),

    updatePassword: async (draft: Draft<ProfileDataPassword>) =>
      new Profile(store, await store.api.put<ProfileData>(`profile/password`, draft.data)),

    updateEmail: async (draft: Draft<ProfileDataEmail>) =>
      new Profile(store, await store.api.put<ProfileData>(`profile/email`, draft.data)),
    signOut: async () => await store.auth.signOut(),
  })
}

export function useCompanies(request: { search?: string } = {}, options?: UseQueryOptions) {
  const { search } = request
  const queries = useQueries()
  const result = useModelQuery<Company[]>(queries.companies.list, {
    ...options,
  })
  return searchResult(result, search)
}

export function useCompany(request: { companyId: number }, options?: UseQueryOptions) {
  const { companyId } = request
  const queries = useQueries()
  return useModelQuery<Company>(queries.companies.detail(companyId), {
    ...options,
    enabled: options?.enabled !== false && !!companyId,
  })
}

export function useCompanyMutation() {
  const store = useStore()
  const queries = useQueries()
  return useModelMutation<CompanyData, Company>(
    {
      create: async (draft) => new Company(store, await store.api.post<CompanyData>(`companies`, draft.data)),
      update: async (draft) =>
        new Company(store, await store.api.put<CompanyData>(`companies/${draft.data.id}`, draft.data)),
      delete: async (draft) => await store.api.delete<void>(`companies/${draft.data.id}`),
    },
    ({ action, draft }) => [
      queries.companies.list.queryKey,
      action === 'update' ? queries.companies.detail(draft.data.id).queryKey : [undefined],
    ],
  )
}

export function useInsightsOverview(
  request: {
    companyId: number
    buildingId: number
    from: Date
    to: Date
    weekdays?: Weekday[]
    workingHours?: boolean
  },
  options?: UseQueryOptions,
) {
  const queries = useQueries()

  const { companyId, buildingId, weekdays, workingHours = false } = request
  const from = format(request.from, 'yyyy-MM-dd')
  const to = format(request.to, 'yyyy-MM-dd')

  const result = useModelQuery<InsightOverview>(
    queries.companies.detail(companyId)._ctx.insightsOverview(buildingId, from, to, workingHours, weekdays),
    {
      ...options,
      enabled: options?.enabled !== false && !!companyId && !!buildingId,
    },
  )
  return result
}

export function useInsightsTime(
  request: {
    companyId: number
    from: Date
    to: Date
    buildingId?: number
    floorId?: number
    weekdays?: Weekday[]
    workingHours?: boolean
    timeType?: InsightTimeType
    workspaceId?: number
  },
  options?: UseQueryOptions,
) {
  const queries = useQueries()

  const { companyId, buildingId, floorId, weekdays, workingHours = false, timeType = 'day', workspaceId } = request
  const from = format(request.from, 'yyyy-MM-dd')
  const to = format(request.to, 'yyyy-MM-dd')

  const result = useModelQuery<InsightTime[]>(
    queries.companies
      .detail(companyId)
      ._ctx.insightsTime(from, to, buildingId, workingHours, weekdays, floorId, timeType, workspaceId),
    {
      ...options,
      enabled: options?.enabled !== false && !!companyId && (!!buildingId || !!floorId),
    },
  )
  return result
}

export function useInsightsFloor(
  request: {
    companyId: number
    floorId: number
    from: Date
    to: Date
    weekdays?: Weekday[]
    workingHours?: boolean
  },
  options?: UseQueryOptions,
) {
  const queries = useQueries()

  const { companyId, floorId, weekdays, workingHours = true } = request
  const from = format(request.from, 'yyyy-MM-dd')
  const to = format(request.to, 'yyyy-MM-dd')

  const result = useModelQuery<InsightWorkspace[]>(
    queries.companies.detail(companyId)._ctx.insightsFloor(floorId, from, to, workingHours, weekdays),
    {
      ...options,
      enabled: options?.enabled !== false && !!companyId && !!floorId,
    },
  )
  return result
}

export function useInsightsTrend(
  request: {
    companyId: number
    buildingId: number
    trendId: InsightTrendId
    from: Date
    to: Date
    weekdays?: Weekday[]
    workingHours?: boolean
  },
  options?: UseQueryOptions,
) {
  const queries = useQueries()

  const { companyId, buildingId, trendId, weekdays, workingHours = false } = request
  const from = format(request.from, 'yyyy-MM-dd')
  const to = format(request.to, 'yyyy-MM-dd')

  const result = useModelQuery<InsightTrend[]>(
    queries.companies.detail(companyId)._ctx.insightsTrend(buildingId, trendId, from, to, workingHours, weekdays),
    {
      ...options,
      enabled: options?.enabled !== false && !!companyId && !!buildingId && !!trendId,
    },
  )
  return result
}

export function useLocations(request: { companyId: number; search?: string }, options?: UseQueryOptions) {
  const queries = useQueries()

  const { companyId, search } = request
  const result = useModelQuery<Location[]>(queries.companies.detail(companyId)._ctx.locations, {
    ...options,
    enabled: options?.enabled !== false && !!companyId,
  })
  return searchResult(result, search)
}

export function useLocation(request: { companyId: number; locationId: number }, options?: UseQueryOptions) {
  const queries = useQueries()

  const { companyId, locationId } = request
  return useModelQuery<Location>(queries.companies.detail(companyId)._ctx.location(locationId), {
    ...options,
    enabled: options?.enabled !== false && !!companyId && !!locationId,
  })
}

export function useLocationMutation(request: { companyId: number }) {
  const store = useStore()
  const queries = useQueries()

  const { companyId } = request
  return useModelMutation<LocationData, Location>(
    {
      create: async (draft) =>
        new Location(store, {
          ...(await store.api.post<LocationData>(
            `company/${companyId || draft.data.company_id}/locations`,
            draft.data,
          )),
          company_id: companyId,
        }),
      update: async (draft) =>
        new Location(store, {
          ...(await store.api.put<LocationData>(`company/${companyId}/locations/${draft.data.id}`, draft.data)),
          company_id: companyId,
        }),
      delete: async (draft) => await store.api.delete<void>(`company/${companyId}/locations/${draft.data.id}`),
    },
    ({ action, draft }) => [
      queries.companies.detail(companyId)._ctx.locations.queryKey,
      action === 'update' ? queries.companies.detail(companyId)._ctx.location(draft.data.id).queryKey : [undefined],
    ],
  )
}

export function useBuildings(
  request: { companyId: number; locationId?: number; search?: string },
  options?: UseQueryOptions,
) {
  const queries = useQueries()

  const { companyId, locationId, search } = request
  const result = useModelQuery<Building[]>(
    locationId
      ? queries.companies.detail(companyId)._ctx.location(locationId)._ctx.buildings
      : queries.companies.detail(companyId)._ctx.buildings,
    {
      ...options,
      enabled: options?.enabled !== false && !!companyId,
    },
  )
  return searchResult(result, search)
}

export function useBuilding(request: { companyId: number; buildingId: number }, options?: UseQueryOptions) {
  const queries = useQueries()

  const { companyId, buildingId } = request
  return useModelQuery<Building>(queries.companies.detail(companyId)._ctx.building(buildingId), {
    ...options,
    enabled: options?.enabled !== false && !!companyId && !!buildingId,
  })
}

export function useBuildingMutation(request: { companyId: number }) {
  const store = useStore()
  const queries = useQueries()

  const { companyId } = request
  return useModelMutation<BuildingData, Building, 'create' | 'update' | 'delete' | 'upload'>(
    {
      create: async (draft) =>
        new Building(store, {
          ...(await store.api.post<BuildingData>(`company/${companyId}/buildings`, draft.data)),
          company_id: companyId,
        }),
      update: async (draft) =>
        new Building(store, {
          ...(await store.api.put<BuildingData>(`company/${companyId}/buildings/${draft.data.id}`, draft.data)),
          company_id: companyId,
        }),
      delete: async (draft) => await store.api.delete<void>(`company/${companyId}/buildings/${draft.data.id}`),
      upload: async (draft: Draft<DataWithUpload<BuildingData>>) => {
        try {
          await uploadBuilding(draft.data, (progress) => {
            draft.update('progress', progress)
          })
        } catch (error) {
          draft.update('progress', 0)
          draft.update('error', error)
          throw error
        }
      },
    },
    ({ action, draft }) => [
      queries.companies.detail(companyId)._ctx.buildings.queryKey,
      action === 'update' || action === 'upload'
        ? queries.companies.detail(companyId)._ctx.building(draft.data.id).queryKey
        : [undefined],
    ],
  )

  function uploadBuilding(data: DataWithUpload<BuildingData>, callback?: ((progress: number) => void) | undefined) {
    const { company_id: companyId, id: buildingId, upload } = data
    const formData = new FormData()
    const file =
      upload instanceof File
        ? upload
        : {
            uri: upload?.uri,
            name: upload?.name,
            type: `image/jpeg`,
          }
    formData.append('file', file as File)
    return store.api.upload<void>(`company/${companyId}/buildings/${buildingId}/photo`, formData, callback)
  }
}

export function useFloors(
  request: { companyId: number; buildingId?: number; search?: string },
  options?: UseQueryOptions,
) {
  const queries = useQueries()

  const { companyId, buildingId, search } = request
  const result = useModelQuery<Floor[]>(
    buildingId
      ? queries.companies.detail(companyId)._ctx.building(buildingId)._ctx.floors
      : queries.companies.detail(companyId)._ctx.floors,
    {
      ...options,
      enabled: options?.enabled !== false && !!companyId,
    },
  )
  return searchResult(result, search)
}

export function useFloor(request: { companyId: number; floorId: number }, options?: UseQueryOptions) {
  const queries = useQueries()

  const { companyId, floorId } = request
  return useModelQuery<Floor>(queries.companies.detail(companyId)._ctx.floor(floorId), {
    ...options,
    enabled: options?.enabled !== false && !!companyId && !!floorId,
  })
}

export function useFloorMutation(request: { companyId: number }) {
  const store = useStore()
  const queries = useQueries()

  const { companyId } = request
  return useModelMutation<FloorData, Floor>(
    {
      create: async (draft) =>
        new Floor(store, {
          ...(await store.api.post<FloorData>(`company/${companyId}/floors`, draft.data)),
          company_id: companyId,
        }),
      update: async (draft) =>
        new Floor(store, {
          ...(await store.api.put<FloorData>(`company/${companyId}/floors/${draft.data.id}`, draft.data)),
          company_id: companyId,
        }),
      delete: async (draft) => await store.api.delete<void>(`company/${companyId}/floors/${draft.data.id}`),
    },
    ({ action, draft }) => [
      queries.companies.detail(companyId)._ctx.floors.queryKey,
      action === 'update' ? queries.companies.detail(companyId)._ctx.floor(draft.data.id).queryKey : [undefined],
    ],
  )
}

export function useFloorplans(request: { companyId: number; buildingId: number }, options?: UseQueryOptions) {
  const queries = useQueries()

  const { companyId, buildingId } = request
  return useModelQuery<Floorplan[]>(queries.companies.detail(companyId)._ctx.building(buildingId)._ctx.floorplans, {
    ...options,
    enabled: options?.enabled !== false && !!companyId && !!buildingId,
  })
}

export function useFloorplan(request: { companyId: number; floorId: number }, options?: UseQueryOptions) {
  const queries = useQueries()

  const { companyId, floorId } = request
  return useModelQuery<Floorplan>(queries.companies.detail(companyId)._ctx.floorplan(floorId), {
    ...options,
    enabled: options?.enabled !== false && !!companyId && !!floorId,
  })
}

export function useFloorplanMutation(request: { companyId: number }) {
  const store = useStore()
  const queries = useQueries()

  const { companyId } = request
  return useModelMutation<
    FloorplanData,
    Floorplan,
    'create' | 'update' | 'delete' | 'upload' | 'publish' | 'discard' | 'createWorkspaces'
  >(
    {
      create: async (draft) =>
        new Floorplan(store, {
          ...(await store.api.post<FloorplanData>(`company/${companyId}/floorplans`, draft.data)),
          company_id: companyId,
        }),
      update: async (draft) =>
        new Floorplan(store, {
          ...(await store.api.put<FloorplanData>(`company/${companyId}/floorplans/${draft.data.id}`, draft.data)),
          company_id: companyId,
        }),
      delete: async (draft) => await store.api.delete<void>(`company/${companyId}/floorplans/${draft.data.id}`),
      upload: async (draft: Draft<DataWithUpload<FloorData>>) => {
        try {
          if (draft.data.upload === null) {
            return await store.api.delete<void>(`company/${companyId}/floorplans/${draft.data.id}/background`)
          }
          return await uploadFloorplanBackground(draft.data, (progress) => {
            draft.update('progress', progress)
          })
        } catch (error) {
          draft.update('progress', 0)
          draft.update('error', error)
          throw error
        }
      },
      publish: async (draft) =>
        new Floorplan(store, {
          ...(await store.api.post<FloorplanData>(`company/${companyId}/floorplans/${draft.data.id}/publish`)),
          company_id: companyId,
        }),
      discard: async (draft) =>
        new Floorplan(store, {
          ...(await await store.api.post<FloorplanData>(`company/${companyId}/floorplans/${draft.data.id}/discard`)),
          company_id: companyId,
        }),
      createWorkspaces: async (draft) =>
        new Floorplan(store, {
          ...draft.data,
          workspaces: await store.api.post<WorkspaceData[]>(
            `company/${companyId}/floorplans/${draft.data.id}/workspaces`,
            draft.data.workspaces,
          ),
        }),
    },
    ({ action, draft }) => [
      queries.companies.detail(companyId)._ctx.building(draft.data.building_id)._ctx.floorplans.queryKey,
      action !== 'create' && action !== 'delete'
        ? queries.companies.detail(companyId)._ctx.floorplan(draft.data.id).queryKey
        : [undefined],
    ],
  )

  async function uploadFloorplanBackground(data: DataWithUpload<FloorplanData>, callback?: (progress: number) => void) {
    const { upload, progress: _, error: __, ...otherData } = data
    const companyId = data.company_id
    const floorId = data.id
    const formData = new FormData()
    const file =
      upload instanceof File
        ? upload
        : {
            uri: upload?.uri,
            name: upload?.name,
            type: `image/svg+xml`,
          }
    formData.append('file', file as File)
    const response = await store.api.upload<{ url: string }>(
      `company/${companyId}/floorplans/${floorId}/background`,
      formData,
      callback,
    )
    return new Floorplan(store, { ...otherData, floorplan: { ...otherData.floorplan, background: response?.url } })
  }
}

export function useFloorplanAsset(uri: string, options?: UseQueryOptions) {
  const queries = useQueries()

  return useModelQuery<{ width: number; height: number; svg: string }>(queries.floorplans.assets(uri), {
    ...options,
    enabled: options?.enabled !== false && !!uri,
  })
}

export function useUsers(request: { companyId: number; search?: string }, options?: UseQueryOptions) {
  const queries = useQueries()

  const { companyId, search } = request
  const result = useModelQuery<User[]>(queries.companies.detail(companyId)._ctx.users, {
    ...options,
    enabled: options?.enabled !== false && !!companyId,
  })
  return searchResult(result, search)
}

export function useUser(request: { companyId: number; userId: number }, options?: UseQueryOptions) {
  const queries = useQueries()

  const { companyId, userId } = request
  return useModelQuery<User>(queries.companies.detail(companyId)._ctx.user(userId), {
    ...options,
    enabled: options?.enabled !== false && !!companyId && !!userId,
  })
}

export function useUserMutation(request: { companyId: number }) {
  const store = useStore()
  const queries = useQueries()

  const { companyId } = request
  return useModelMutation<UserData | UserInviteData, User, 'invite' | 'reinvite' | 'revoke' | 'update' | 'delete'>(
    {
      invite: (draft) => store.auth.inviteUser(companyId, draft.data as UserInviteData),
      reinvite: (draft) => store.auth.reinviteUser(companyId, draft.data as UserInviteData),
      revoke: async (draft) => await store.api.delete<void>(`company/${companyId}/users/${draft.data.id}/revoke`),
      update: async (draft) =>
        new User(store, await store.api.put<UserData>(`company/${companyId}/users/${draft.data.id}`, draft.data)),
      delete: async (draft) => await store.api.delete<void>(`company/${companyId}/users/${draft.data.id}`),
    },
    ({ action, draft }) => [
      queries.companies.detail(companyId)._ctx.users.queryKey,
      action === 'update' ? queries.companies.detail(companyId)._ctx.user(draft.data.id).queryKey : [undefined],
    ],
  )
}

export function useWorkspaces(
  request: {
    companyId: number
    pagination?: TablePagination
    sort?: TableSort
    filter?: WorkspacesFilter
    notInZone?: boolean
  },
  options?: UseQueryOptions,
) {
  const queries = useQueries()

  const { companyId, pagination, sort, filter, notInZone } = request
  const result = useModelQuery<{
    data: Workspace[]
    meta?: Meta
  }>(queries.companies.detail(companyId)._ctx.workspaces(pagination, sort, filter, notInZone), {
    ...options,
    enabled: options?.enabled !== false && !!companyId,
  })
  return result
}

export function useInfiniteWorkspaces(
  request: {
    companyId: number
    pagination?: TablePagination
    sort?: TableSort
    filter?: WorkspacesFilter
  },
  options?: UseInfiniteQueryOptions,
) {
  const queries = useQueries()

  const { companyId, pagination = { page: 0, size: 10 }, sort, filter } = request

  const result = useModelInfiniteQuery<{ data: Workspace[]; meta?: Meta }>(
    queries.companies.detail(companyId)._ctx.workspaces(pagination, sort, filter)._ctx.infinite,
    {
      ...options,
      enabled: options?.enabled !== false && !!companyId,
    },
  )
  return result
}

export function useWorkspace(request: { companyId: number; workspaceId: number }, options?: UseQueryOptions) {
  const queries = useQueries()

  const { companyId, workspaceId } = request
  return useModelQuery<Workspace>(queries.companies.detail(companyId)._ctx.workspace(workspaceId), {
    ...options,
    enabled: options?.enabled !== false && !!companyId && !!workspaceId,
  })
}

export function useWorkspaceMutation(data: { companyId: number }) {
  const store = useStore()
  const queries = useQueries()

  const { companyId } = data
  return useModelMutation<WorkspaceData, Workspace>(
    {
      create: async (draft) =>
        new Workspace(store, await store.api.post<WorkspaceData>(`company/${companyId}/workspaces`, draft.data)),
      update: async (draft) =>
        new Workspace(
          store,
          await store.api.put<WorkspaceData>(`company/${companyId}/workspaces/${draft.data.id}`, draft.data),
        ),
      delete: async (draft) => await store.api.delete<void>(`company/${companyId}/workspaces/${draft.data.id}`),
    },
    ({ action, draft }) => [
      [...queries.companies.detail(companyId).queryKey, 'workspaces'],
      action === 'update' ? queries.companies.detail(companyId)._ctx.workspace(draft.data.id).queryKey : [undefined],
      draft.data.floor_id
        ? queries.companies.detail(companyId)._ctx.floorplan(draft.data.floor_id).queryKey
        : [undefined],
    ],
  )
}

export function useWorkspaceSchedule(request: { companyId: number; workspaceId: number }, options?: UseQueryOptions) {
  const queries = useQueries()

  const { companyId, workspaceId } = request
  return useModelQuery<WorkspaceSchedule>(
    queries.companies.detail(companyId)._ctx.workspace(workspaceId)._ctx.schedule,
    {
      ...options,
      enabled: options?.enabled !== false && !!companyId && !!workspaceId,
    },
  )
}

export function useWorkspaceTriggers(request: { companyId: number; workspaceId: number }, options?: UseQueryOptions) {
  const queries = useQueries()

  const { companyId, workspaceId } = request
  return useModelQuery<Trigger[]>(queries.companies.detail(companyId)._ctx.workspace(workspaceId)._ctx.triggers, {
    ...options,
    enabled: options?.enabled !== false && !!companyId && !!workspaceId,
  })
}

export function useWorkspaceTypes(request: { search?: string } = {}, options?: UseQueryOptions) {
  const queries = useQueries()

  const { search } = request
  const result = useModelQuery<WorkspaceType[]>(queries.workspaceTypes.list, {
    ...options,
    enabled: options?.enabled !== false,
  })
  return searchResult(result, search)
}

export function useWorkspaceType(request: { workspaceTypeId: number }, options?: UseQueryOptions) {
  const queries = useQueries()

  const { workspaceTypeId } = request
  return useModelQuery<WorkspaceType>(queries.workspaceTypes.detail(workspaceTypeId), {
    ...options,
    enabled: options?.enabled !== false && !!workspaceTypeId,
  })
}

export function useWorkspaceTypeMutation() {
  const store = useStore()
  const queries = useQueries()

  return useModelMutation<WorkspaceTypeData, WorkspaceType>(
    {
      create: async (draft) =>
        new WorkspaceType(store, await store.api.post<WorkspaceTypeData>(`workspacetypes`, draft.data)),
      update: async (draft) =>
        new WorkspaceType(store, await store.api.put<WorkspaceTypeData>(`workspacetypes/${draft.data.id}`, draft.data)),
      delete: async (draft) => await store.api.delete<void>(`workspacetypes/${draft.data.id}`),
    },
    ({ action, draft }) => [
      queries.workspaceTypes.list.queryKey,
      action === 'update' ? queries.workspaceTypes.detail(draft.data.id).queryKey : [undefined],
    ],
  )
}

export function useWorkspaceTypeElements(options?: UseQueryOptions) {
  const queries = useQueries()

  const result = useModelQuery<WorkspaceTypeElement[]>(queries.elements.list, {
    ...options,
    enabled: options?.enabled !== false,
  })
  return result
}

export function useWorkspaceTypeElement(request: { elementId: number }, options?: UseQueryOptions) {
  const queries = useQueries()

  const { elementId } = request
  const result = useModelQuery<WorkspaceTypeElement>(queries.elements.detail(elementId), {
    ...options,
    enabled: options?.enabled !== false && !!elementId,
  })
  return result
}

export function useZones(
  request: { companyId: number; workspaceId?: number; floorId?: number; search?: string },
  options?: UseQueryOptions,
) {
  const queries = useQueries()

  const { companyId, workspaceId, floorId, search } = request
  const result = useModelQuery<Zone[]>(
    workspaceId
      ? queries.companies.detail(companyId)._ctx.workspace(workspaceId)._ctx.zones
      : floorId
        ? queries.companies.detail(companyId)._ctx.zones._ctx.floor(floorId)
        : queries.companies.detail(companyId)._ctx.zones,
    {
      ...options,
      enabled: options?.enabled !== false && !!companyId,
    },
  )
  return searchResult(result, search)
}

export function useZone(request: { companyId: number; zoneId: number }, options?: UseQueryOptions) {
  const queries = useQueries()

  const { companyId, zoneId } = request
  return useModelQuery<Zone>(queries.companies.detail(companyId)._ctx.zone(zoneId), {
    ...options,
    enabled: options?.enabled !== false && !!companyId && !!zoneId,
  })
}

export function useZoneMutation(request: { companyId: number }) {
  const store = useStore()
  const queries = useQueries()

  const { companyId } = request
  return useModelMutation<ZoneData, Zone>(
    {
      create: async (draft) =>
        new Zone(store, {
          ...(await store.api.post<ZoneData>(`company/${companyId}/zones`, draft.data)),
          company_id: companyId,
        }),
      update: async (draft) =>
        new Zone(store, {
          ...(await store.api.put<ZoneData>(`company/${companyId}/zones/${draft.data.id}`, draft.data)),
          company_id: companyId,
        }),
      delete: async (draft) => store.api.delete<void>(`company/${companyId}/zones/${draft.data.id}`),
    },
    ({ action, draft }) => [
      queries.companies.detail(companyId)._ctx.zones.queryKey,
      action === 'update' ? queries.companies.detail(companyId)._ctx.zone(draft.data.id).queryKey : [undefined],
    ],
  )
}

export function useZoneWorkspacesMutation(request: { companyId: number; zoneId: number }) {
  const queries = useQueries()
  const store = useStore()

  const { companyId, zoneId } = request
  return useModelMutation<{ id: number; workspaces: number[] } | WorkspaceData, any, 'add' | 'delete'>(
    {
      add: async (draft) => {
        await store.api.post<string[]>(`company/${companyId}/zones/${zoneId}/workspaces`, {
          workspaces: (draft as Draft<{ id: number; workspaces: number[] }>).data.workspaces,
        })
      },
      delete: async (draft) => {
        await store.api.delete<string[]>(`company/${companyId}/workspaces/${draft.data.id}/zones/${zoneId}`)
      },
    },
    () => [
      queries.companies.detail(companyId)._ctx.zone(zoneId).queryKey,
      queries.companies.detail(companyId)._ctx.zones.queryKey,
    ],
  )
}
export function useProducts(
  request: {
    companyId: number
    pagination?: TablePagination
    sort?: TableSort
    filter?: ProductsFilter
  },
  options?: UseQueryOptions,
) {
  const queries = useQueries()

  const { companyId, pagination, sort, filter } = request
  const result = useModelQuery<{ data: Product[]; meta?: Meta }>(
    queries.companies.detail(companyId)._ctx.products(pagination, sort, filter),
    {
      ...options,
      enabled: options?.enabled !== false && !!companyId,
    },
  )
  return result
}

export function useInfiniteProducts(
  request: { companyId: number; pagination?: TablePagination; sort?: TableSort; filter?: ProductsFilter },
  options?: UseInfiniteQueryOptions,
) {
  const queries = useQueries()

  const { companyId, pagination = { page: 0, size: 10 }, sort, filter } = request
  const result = useModelInfiniteQuery<{ data: Product[]; meta?: Meta }>(
    queries.companies.detail(companyId)._ctx.products(pagination, sort, filter)._ctx.infinite,
    {
      ...options,
      enabled: options?.enabled !== false && !!companyId,
    },
  )
  return result
}

export function useProduct(request: { companyId: number; productId?: number }, options?: UseQueryOptions) {
  const queries = useQueries()

  const { companyId, productId } = request
  return useModelQuery<Product>(queries.companies.detail(companyId)._ctx.product(productId!), {
    ...options,
    enabled: options?.enabled !== false && !!companyId && !!productId,
  })
}

export function useProductMutation(data: { companyId: number }) {
  const queries = useQueries()
  const store = useStore()

  const { companyId } = data
  return useModelMutation<ProductData, Product, 'create' | 'update' | 'delete' | 'upload'>(
    {
      create: async (draft: Draft<ProductData>) =>
        new Product(store, await store.api.post<ProductData>(`company/${companyId}/products`, draft.data)),
      update: async (draft: Draft<ProductData>) =>
        new Product(
          store,
          await store.api.put<ProductData>(`company/${companyId}/products/${draft.data.id}`, draft.data),
        ),
      delete: async (draft: Draft<ProductData>) =>
        await store.api.delete<void>(`company/${companyId}/products/${draft.data.id}`),
      upload: async (draft: Draft<DataWithUpload<ProductData>>) => await uploadProduct(companyId, draft.data),
    },
    ({ action, draft }) => [
      [...queries.companies.detail(companyId).queryKey, 'products'],
      ...(draft.changedData?.hasOwnProperty('workspace_id')
        ? [
            [...queries.companies.detail(companyId).queryKey, 'workspaces'],
            queries.companies.detail(companyId)._ctx.workspace(draft.data.workspace_id!).queryKey,
          ]
        : []),
      action !== 'create' && action !== 'delete'
        ? queries.companies.detail(companyId)._ctx.product(draft.data.id).queryKey
        : [undefined],
    ],
  )

  function uploadProduct(companyId: number, data: DataWithUpload<ProductData>) {
    const { upload } = data
    const formData = new FormData()
    const file =
      upload instanceof File
        ? upload
        : {
            uri: upload?.uri,
            name: upload?.name,
            type: `image/jpeg`,
          }
    formData.append('file', file as File)
    return store.api.post<void>(`company/${companyId}/products/import`, formData)
  }
}

export function useProductTransferMutation(data: { companyId: number }) {
  const queries = useQueries()
  const store = useStore()

  const { companyId } = data
  return useModelMutation<ProductTransferData, any, 'transfer'>(
    {
      transfer: async (draft: Draft<ProductTransferData>) =>
        await store.api.post<void>(`company/${companyId}/products/transfer`, {
          company_id: (draft.data as ProductTransferData).transfer_company_id,
          products: [draft.data.id],
        }),
    },
    ({ draft }) => [
      [...queries.companies.detail(companyId).queryKey, 'products'],
      queries.companies.detail(companyId)._ctx.product(draft.data.id).queryKey,
    ],
  )
}

export function useProductLogging(
  request: {
    companyId: number
    productId: number
    pagination?: TablePagination
    sort?: TableSort
  },
  options?: UseQueryOptions,
) {
  const queries = useQueries()

  const {
    companyId,
    productId,
    pagination = { page: 0, size: 10 },
    sort = { field: 'updated_at', direction: 'asc' },
  } = request
  const result = useModelQuery<{ data: LoggingMessage[]; meta: Meta }>(
    queries.companies.detail(companyId)._ctx.product(productId)._ctx.logging(pagination, sort),
    {
      ...options,
      enabled: options?.enabled !== false && !!companyId && !!productId,
    },
  )
  return result
}

export function useInfiniteProductLogging(
  request: {
    companyId: number
    productId: number
    pagination?: TablePagination
    sort?: TableSort
  },
  options?: UseInfiniteQueryOptions,
) {
  const queries = useQueries()

  const {
    companyId,
    productId,
    pagination = { page: 0, size: 10 },
    sort = { field: 'updated_at', direction: 'asc' },
  } = request
  return useModelInfiniteQuery<{ data: LoggingMessage[]; meta: Meta }>(
    queries.companies.detail(companyId)._ctx.product(productId)._ctx.logging(pagination, sort)._ctx.infinite,
    {
      ...options,
      enabled: options?.enabled !== false && !!companyId && !!productId,
    },
  )
}

export function useProductTypes(options?: UseQueryOptions) {
  const queries = useQueries()

  const result = useModelQuery<ProductType[]>(queries.productTypes.list, {
    ...options,
  })
  return result
}

export function useSchedules(request: { companyId: number; search?: string }, options?: UseQueryOptions) {
  const queries = useQueries()

  const { companyId, search } = request
  const result = useModelQuery<Schedule[]>(queries.companies.detail(companyId)._ctx.schedules, {
    ...options,
    enabled: options?.enabled !== false && !!companyId,
  })
  return searchResult(result, search)
}

export function useSchedule(request: { companyId: number; scheduleId: number }, options?: UseQueryOptions) {
  const queries = useQueries()

  const { companyId, scheduleId } = request
  return useModelQuery<Schedule>(queries.companies.detail(companyId)._ctx.schedule(scheduleId), {
    ...options,
    enabled: options?.enabled !== false && !!companyId && !!scheduleId,
  })
}

export function useScheduleMutation(request: { companyId: number }) {
  const queries = useQueries()
  const store = useStore()

  const { companyId } = request
  return useModelMutation<ScheduleData, Schedule>(
    {
      create: async (draft) =>
        new Schedule(store, {
          ...(await store.api.post<ScheduleData>(`company/${companyId}/settings/schedule`, draft.data)),
          company_id: companyId,
        }),
      update: async (draft) =>
        new Schedule(store, {
          ...(await store.api.put<ScheduleData>(`company/${companyId}/settings/schedule/${draft.data.id}`, draft.data)),
          company_id: companyId,
        }),
      delete: async (draft) => await store.api.delete<void>(`company/${companyId}/settings/schedule/${draft.data.id}`),
    },
    ({ action, draft }) => [
      queries.companies.detail(companyId)._ctx.schedules.queryKey,
      action === 'update' ? queries.companies.detail(companyId)._ctx.schedule(draft.data.id).queryKey : [undefined],
    ],
  )
}

export function useComments(
  request:
    | {
        companyId: number
        workspaceId?: number
        pagination?: TablePagination
      }
    | {
        companyId: number
        productId?: number
        pagination?: TablePagination
      },
  options?: UseQueryOptions,
) {
  const queries = useQueries()

  const { companyId, workspaceId, productId, pagination = { page: 0, size: 10 } } = request as any
  return useModelQuery<Comment[]>(
    productId
      ? queries.companies.detail(companyId)._ctx.product(productId)._ctx.comments(pagination)
      : queries.companies.detail(companyId)._ctx.workspace(workspaceId)._ctx.comments(pagination),
    {
      ...options,
      enabled: options?.enabled !== false && !!companyId && !!(workspaceId || productId),
    },
  )
}

export function useCommentMutation(request: { companyId: number; locationId: number }) {
  const queries = useQueries()
  const store = useStore()

  const { companyId, locationId } = request
  return useModelMutation<CommentData, Comment, 'create'>(
    {
      create: async (draft) =>
        new Comment(store, {
          ...(await store.api.post<CommentData>(`company/${companyId}/locations/${locationId}/comment`, draft.data)),
          company_id: companyId,
        }),
    },
    ({ draft }) =>
      draft.data.product_id
        ? [queries.companies.detail(companyId)._ctx.product(draft.data.product_id).queryKey]
        : draft.data.workspace_id
          ? [queries.companies.detail(companyId)._ctx.workspace(draft.data.workspace_id).queryKey]
          : [],
  )
}

export function usePhotos(
  request:
    | {
        companyId: number
        workspaceId?: number
        pagination?: TablePagination
      }
    | {
        companyId: number
        productId?: number
        pagination?: TablePagination
      },
  options?: UseQueryOptions,
) {
  const queries = useQueries()

  const { companyId, workspaceId, productId, pagination = { page: 0, size: 10 } } = request as any
  return useModelQuery<Photo[]>(
    productId
      ? queries.companies.detail(companyId)._ctx.product(productId)._ctx.photos(pagination)
      : queries.companies.detail(companyId)._ctx.workspace(workspaceId)._ctx.photos(pagination),
    {
      ...options,
      enabled: options?.enabled !== false && !!companyId && !!(workspaceId || productId),
    },
  )
}

export function usePhotoMutation(request: { companyId: number; locationId: number }) {
  const queries = useQueries()
  const store = useStore()

  const { companyId, locationId } = request
  return useModelMutation<PhotoData, Photo, 'create'>(
    {
      create: async (draft: Draft<DataWithUpload<PhotoData>>) => {
        try {
          const data = await uploadPhoto(locationId, draft.data, (progress) => {
            draft.update('progress', progress)
          })
          return new Photo(store, {
            ...data,
            company_id: companyId,
          })
        } catch (error) {
          draft.update('progress', 0)
          draft.update('error', error)
          throw error
        }
      },
    },
    ({ draft }) =>
      draft.data.product_id
        ? [queries.companies.detail(companyId)._ctx.product(draft.data.product_id).queryKey]
        : draft.data.workspace_id
          ? [queries.companies.detail(companyId)._ctx.workspace(draft.data.workspace_id).queryKey]
          : [],
  )

  function uploadPhoto(
    locationId: number,
    data: DataWithUpload<PhotoData>,
    callback?: ((progress: number) => void) | undefined,
  ) {
    const { company_id, workspace_id, product_id, upload } = data
    const formData = new FormData()
    const file =
      upload instanceof File
        ? upload
        : {
            uri: upload?.uri,
            name: upload?.name,
            type: `image/jpeg`,
          }
    formData.append('file', file as File)
    if (workspace_id) formData.append('workspace_id', `${workspace_id}`)
    if (product_id) formData.append('product_id', `${product_id}`)

    return store.api.upload<PhotoData>(`company/${company_id}/locations/${locationId}/photo`, formData, callback)
  }
}

export function useTimeZone(request: { lat: number; lng: number }, options?: UseQueryOptions) {
  const queries = useQueries()

  return useModelQuery<TimeZone | undefined>(queries.timezone.detail(request.lat, request.lng), {
    ...options,
    enabled: options?.enabled !== false && !!request.lat && !!request.lng,
  })
}

export function useCountry(request: { lat: number; lng: number }, options?: UseQueryOptions) {
  const queries = useQueries()

  return useModelQuery<Country | undefined>(queries.country.detail(request.lat, request.lng), {
    ...options,
    enabled: options?.enabled !== false && !!request.lat && !!request.lng,
  })
}
